import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

/**
 * One clock for the whole console.
 *
 * Sixty rows each running their own `setInterval` is sixty timers, sixty
 * renders a second, and a board that stutters on a laptop in Low Power Mode.
 * One interval publishes through context, and because a context update pierces
 * `memo`, only the components that actually read the clock — the timer cells —
 * re-render. The row around them does not.
 */
const NowContext = createContext<number>(Date.now());

export function NowProvider({ children }: { children: ReactNode }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      clearInterval(id);
    };
  }, []);

  return <NowContext.Provider value={now}>{children}</NowContext.Provider>;
}

export function useNow(): number {
  return useContext(NowContext);
}
